'use client';

import Link from 'next/link';
import { useEffect, useMemo } from 'react';
import { Markdown } from '@/components/Markdown';
import { Histogram } from '@/components/Shared/Charts/Histogram';
import { DataList } from '@/components/Shared/DataList';
import { TableColumn } from '@/lib/types/TableColumn';
import { TableColumnStatistics } from '@/lib/types/TableColumnStatistics';
import { TableSampleRow } from '@/lib/types/TableSample';
import { cn } from '@/lib/utils';
import { NotAvailable } from '../../Shared/NotAvailable';
interface TableFooterDetailsItemProps {
  sample: TableSampleRow[];
  columnWithStats: Omit<TableColumn, 'description' | 'title'> & {
    description?: string;
    title?: string;
  } & TableColumnStatistics;
}
export const classes = {
  wrapper: 'p-6',
  column: 'flex flex-col gap-5',
  half: 'w-full md:w-6/12 flex flex-col gap-5',
  group: '',
  label: 'font-bold',
  value: '[overflow-wrap:anywhere] font-mono font-normal text-xs tracking-wide'
};
export const TableDetailsItem = ({
  sample,
  columnWithStats
}: TableFooterDetailsItemProps) => {
  const sampleValues = useMemo(() => sample.reduce((acc: string[], row) => {
    const value: string = String(row[columnWithStats.name] ?? '');
    if (!acc.includes(value) && value.length > 0) {
      acc.push(value);
    }
    return acc;
  }, []), [sample, columnWithStats.name]);
  const id = `table-details-column-${columnWithStats.name}-statistics`;
  const {
    name,
    description,
    datatype,
    title,
    ...data
  } = columnWithStats;
  const histogram = columnWithStats.length_histogram ?? columnWithStats.histogram;
  const hash = `column-${columnWithStats.name}`;
  return <div id={hash} className={classes.wrapper}>
      <div className={classes.column}>
        <div className="flex flex-col md:flex-row gap-10">
          <div className={classes.half}>
            <div className={classes.group}>
              <Link prefetch={false} href={{
              hash
            }}>
                <h5 className="lowercase leading-tight break-words">
                  {columnWithStats.name}
                </h5>
              </Link>
              <p className={cn(classes.value, 'prose')}>
                {columnWithStats.datatype}
              </p>
            </div>

            <div className={classes.group}>
              <h6 className={classes.label}>Sample values</h6>
              <p className={cn(classes.value, 'prose line-clamp-6')}>
                {sampleValues.reverse().join(', ')}
              </p>
            </div>

            <div className={classes.group}>
              <h6 className={classes.label}>Details</h6>

              <Markdown className={cn('prose prose-p:[overflow-wrap:anywhere] prose-p:text-xs prose-p:font-mono prose-p:font-normal', classes.value)}>
                {columnWithStats.description}
              </Markdown>
            </div>
          </div>
          <div className={classes.half}>
            <div className={classes.group}>
              <h6 className={cn(classes.label, 'pb-5')}>Histogram</h6>
              <Histogram data={histogram} />
            </div>

            <div className={classes.group}>
              <DataList showFallback showMissing id={id} data={data as Record<string, any>} />
            </div>
          </div>
        </div>
      </div>
    </div>;
};